import { AxiosInstance } from 'axios';
import { handleServiceError } from '@sdk/utils/error-handler';
import { poll } from '@sdk/utils';
import type {
  BackendAccount,
  CheckUsernameRequest,
  UpdateUsernameRequest,
  LinkAccountRequest,
  UpdateEmailRequest,
} from '@sdk/shared/types/settings.types';

export class SettingsService {
  private protectedAxios: AxiosInstance;
  private publicAxios: AxiosInstance;

  constructor(protectedAxios: AxiosInstance, publicAxios: AxiosInstance) {
    this.protectedAxios = protectedAxios;
    this.publicAxios = publicAxios;
  }

  getAccount = async (jwt?: string) => {
    const fetchAccount = async (): Promise<BackendAccount> => {
      const res = jwt
        ? await this.publicAxios.get<BackendAccount>('/auth/account', {
            headers: { Authorization: `Bearer ${jwt}` },
          })
        : await this.protectedAxios.get<BackendAccount>('/auth/account');
      if (!res.data) throw new Error('Failed to get account details');
      return res.data;
    };
    return handleServiceError(async () => {
      const res = await poll(fetchAccount, 1000, 5);
      if (!res) throw new Error('Failed to get account details');
      return res;
    });
  };

  checkUsername = async (payload: CheckUsernameRequest) => {
    return handleServiceError(async () => {
      const { data } = await this.protectedAxios.post<null>('/auth/username/check', payload);
      if (data) throw new Error('Failed to check username availability');
      return data;
    });
  };

  updateUsername = async (payload: UpdateUsernameRequest) => {
    return handleServiceError(async () => {
      const { data } = await this.protectedAxios.post<BackendAccount>('/auth/username', payload);
      if (!data) throw new Error('Failed to update username');
      return data;
    });
  };

  updateEmail = async (payload: UpdateEmailRequest) => {
    return handleServiceError(async () => {
      const { data } = await this.protectedAxios.post<BackendAccount>('/auth/email', payload);
      if (!data) throw new Error('Failed to update email');
      return data;
    });
  };

  linkAccount = async (payload: LinkAccountRequest) => {
    return handleServiceError(async () => {
      const { data } = await this.protectedAxios.post<BackendAccount>('/auth/link', payload);
      if (!data) throw new Error('Failed to link accounts');
      return data;
    });
  };

  isWhitelisted = async (payload: { id: string; jwt?: string }) => {
    return handleServiceError(async () => {
      const { data } = await this.protectedAxios.post<boolean>(
        '/auth/allowed',
        { id: payload.id },
        {
          headers: {
            ...(payload.jwt && { Authorization: `Bearer ${payload.jwt}` }),
          },
        },
      );
      if (!data) throw new Error('Failed to check if user is whitelisted');
      return data;
    });
  };
}
